import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../services/firebase';
import { ref, get, update } from 'firebase/database';

const AddressDetails = () => {
  const navigate = useNavigate();
  const { currentUser, userDetails } = useAuth();
  const [formData, setFormData] = useState({
    streetAddress: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    currentLocation: '',
    preferredLocations: '',
    willingToRelocate: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      navigate('/login');
      return;
    }

    // Fetch existing address data
    const fetchAddressData = async () => {
      try {
        const userRef = ref(database, `users/${userId}`);
        const snapshot = await get(userRef);
        
        if (snapshot.exists()) {
          const userData = snapshot.val();
          setFormData(prevData => ({
            ...prevData,
            streetAddress: userData.streetAddress || '',
            city: userData.city || '',
            state: userData.state || '',
            country: userData.country || '',
            pincode: userData.pincode || '',
            currentLocation: userData.currentLocation || '',
            preferredLocations: userData.preferredLocations || '',
            profileStep: 3,
            willingToRelocate: userData.willingToRelocate || false
          }));
        }
      } catch (error) {
        console.error('Error fetching address data:', error);
        setError('Failed to load address data');
      } finally {
        setLoading(false);
      }
    };

    fetchAddressData();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        throw new Error('User not found');
      }

      // Update user data directly in users node
      const userRef = ref(database, `users/${userId}`);
      const updates = {
        streetAddress: formData.streetAddress,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        pincode: formData.pincode,
        currentLocation: formData.currentLocation,
        preferredLocations: formData.preferredLocations,
        willingToRelocate: formData.willingToRelocate,
        profileStep: 3,
        updatedAt: new Date().toISOString()
      };
      await update(userRef, updates);
      
      // Wait for the update to complete before navigating
      setTimeout(() => {
        localStorage.setItem('profileStep', '3');
        navigate('/profile/education');
      }, 500);
      
    } catch (error) {
      console.error('Error updating address:', error);
      setError('Failed to update address information');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  return (
    <div className="min-h-screen bg-[#f8f9fe]">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200">
        <div className="h-full w-2/4 bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300"></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        {/* Header with Modern Design */}
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Where Are You Located?
          </h2>
          <div className="flex items-center justify-center space-x-8 mb-8">
            <div className="flex items-center opacity-50">
              <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-semibold">✓</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-600">Personal Info</p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-semibold">2</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-indigo-600">Address</p>
              </div>
            </div>
            {[3, 4].map(step => (
              <div key={step} className="flex items-center opacity-50">
                <div className="w-8 h-8 rounded-full border-2 border-gray-300 text-gray-400 flex items-center justify-center font-semibold">{step}</div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-400">Step {step}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main Form with Modern Card Design */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <form onSubmit={handleSubmit} className="divide-y divide-gray-100">
            {/* Current Address Section */}
            <div className="p-8 transition duration-300 hover:bg-gray-50">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Current Address</h3>
              </div>

              <div className="space-y-6">
                <div className="group">
                  <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address
                  </label>
                  <input
                    id="streetAddress"
                    name="streetAddress"
                    type="text"
                    required
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-200 group-hover:border-indigo-200"
                    value={formData.streetAddress}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                  <div className="group">
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      required
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-200 group-hover:border-indigo-200"
                      value={formData.city}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="group">
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      id="state"
                      name="state"
                      type="text"
                      required
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-200 group-hover:border-indigo-200"
                      value={formData.state}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                  <div className="group">
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <input
                      id="country"
                      name="country"
                      type="text"
                      required
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-200 group-hover:border-indigo-200"
                      value={formData.country}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="group">
                    <label htmlFor="pincode" className="block text-sm font-medium text-gray-700 mb-2">
                      Pincode
                    </label>
                    <input
                      id="pincode"
                      name="pincode"
                      type="text"
                      required
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-200 group-hover:border-indigo-200"
                      value={formData.pincode}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Job Location Preferences */}
            <div className="p-8 transition duration-300 hover:bg-gray-50">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Job Location Preferences</h3>
              </div>

              <div className="space-y-6">
                <div className="group">
                  <label htmlFor="currentLocation" className="block text-sm font-medium text-gray-700 mb-2">
                    Current Location
                  </label>
                  <input
                    id="currentLocation"
                    name="currentLocation"
                    type="text"
                    required
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-200 group-hover:border-indigo-200"
                    value={formData.currentLocation}
                    onChange={handleChange}
                  />
                </div>

                <div className="group">
                  <label htmlFor="preferredLocations" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Job Locations
                  </label>
                  <input
                    id="preferredLocations"
                    name="preferredLocations"
                    type="text"
                    placeholder="e.g., New York, London, Singapore"
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-200 group-hover:border-indigo-200"
                    value={formData.preferredLocations}
                    onChange={handleChange}
                  />
                  <p className="mt-2 text-sm text-gray-500">Separate multiple locations with commas</p>
                </div>

                <div className="flex items-center space-x-3 group">
                  <input
                    id="willingToRelocate"
                    name="willingToRelocate"
                    type="checkbox"
                    className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded transition duration-200"
                    checked={formData.willingToRelocate}
                    onChange={handleChange}
                  />
                  <label htmlFor="willingToRelocate" className="text-sm text-gray-900">
                    I am willing to relocate for job opportunities
                  </label>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="px-8 py-6 bg-gray-50">
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => navigate('/profile/personal')}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-8 py-3 border border-transparent shadow-sm text-base font-medium rounded-xl text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                >
                  Next Step
                  <svg className="ml-2 -mr-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddressDetails;